import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { CheckCircle2, XCircle, Loader2, LocateFixed, MapPin, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { validatePostcode, nearestPostcode, lookupPostcode, type IdealAddress, RateLimitError } from "@/lib/idealPostcodes";
import { useToast } from "@/hooks/use-toast";

interface ValidatedPostcodeInputProps {
  value: string;
  onChange: (value: string) => void;
  onValidated?: (valid: boolean) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  "aria-label"?: string;
  /** Show a "Use my location" button that fills the postcode via geolocation */
  showUseMyLocation?: boolean;
}

const UK_POSTCODE_REGEX = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i;

const ValidatedPostcodeInput = ({
  value,
  onChange,
  onValidated,
  placeholder = "e.g. SW1A 1AA",
  className,
  id,
  onKeyDown,
  "aria-label": ariaLabel,
  showUseMyLocation = false,
}: ValidatedPostcodeInputProps) => {
  const [status, setStatus] = useState<"idle" | "checking" | "valid" | "invalid">("idle");
  const [details, setDetails] = useState<IdealAddress | null>(null);
  const [locating, setLocating] = useState(false);
  const [throttled, setThrottled] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const lastChecked = useRef("");
  const { toast } = useToast();

  useEffect(() => {
    const cleaned = value.replace(/\s/g, "").toUpperCase();

    if (cleaned.length < 5) {
      setStatus("idle");
      setDetails(null);
      onValidated?.(false);
      return;
    }

    if (!UK_POSTCODE_REGEX.test(value)) {
      setStatus("invalid");
      setDetails(null);
      onValidated?.(false);
      return;
    }

    if (cleaned === lastChecked.current) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    setStatus("checking");
    setDetails(null);

    debounceRef.current = setTimeout(async () => {
      try {
        // Single lookup serves as both validation and details fetch.
        const addresses = await lookupPostcode(cleaned);
        lastChecked.current = cleaned;
        const valid = addresses.length > 0;
        setStatus(valid ? "valid" : "invalid");
        setDetails(valid ? addresses[0] : null);
        onValidated?.(valid);
      } catch (e) {
        if (e instanceof RateLimitError) {
          setThrottled(true);
          setStatus("idle");
          toast({ title: "Too many lookups", description: e.message, variant: "destructive" });
        }
      }
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, onValidated]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setThrottled(false);
    onChange(e.target.value.toUpperCase());
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      toast({ title: "Location unavailable", description: "Your browser does not support geolocation.", variant: "destructive" });
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const pc = await nearestPostcode(coords.latitude, coords.longitude);
          if (pc) {
            onChange(pc.toUpperCase());
          } else {
            toast({ title: "No postcode found", description: "We couldn't find a UK postcode for your location.", variant: "destructive" });
          }
        } catch (e) {
          if (e instanceof RateLimitError) {
            setThrottled(true);
            toast({ title: "Too many lookups", description: e.message, variant: "destructive" });
          } else {
            toast({ title: "Lookup failed", description: "Could not look up your postcode. Please try again.", variant: "destructive" });
          }
        } finally {
          setLocating(false);
        }
      },
      (err) => {
        setLocating(false);
        toast({
          title: "Location denied",
          description: err.code === err.PERMISSION_DENIED
            ? "Please allow location access to use this feature."
            : "Could not retrieve your location.",
          variant: "destructive",
        });
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 },
    );
  };

  return (
    <div className="relative">
      <div className="relative">
        <Input
          id={id}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          className={cn(showUseMyLocation ? "pr-40" : "pr-9", className)}
          onKeyDown={onKeyDown}
          aria-label={ariaLabel}
        />
        <div className="absolute right-2 inset-y-0 flex items-center gap-2 leading-none pointer-events-none">
          {status === "checking" && <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />}
          {status === "valid" && <CheckCircle2 className="w-4 h-4 text-green-500" />}
          {status === "invalid" && <XCircle className="w-4 h-4 text-destructive" />}
          {showUseMyLocation && (
            <button
              type="button"
              onClick={handleUseMyLocation}
              disabled={locating}
              className="pointer-events-auto inline-flex h-6 items-center gap-1 text-xs font-medium leading-none text-primary hover:underline disabled:opacity-60 whitespace-nowrap"
              aria-label="Use my location"
            >
              {locating ? <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" /> : <LocateFixed className="w-3.5 h-3.5 shrink-0" />}
              Use my location
            </button>
          )}
        </div>
      </div>
      {status === "invalid" && value.length >= 5 && (
        <p className="text-xs text-destructive mt-1 absolute left-0 top-full">Please enter a valid UK postcode</p>
      )}
      {status === "valid" && details && (
        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1 absolute left-0 top-full right-0">
          <MapPin className="w-3 h-3 shrink-0" />
          <span className="truncate">
            {[details.post_town, details.county, details.country].filter(Boolean).join(", ")}
          </span>
        </p>
      )}
      {throttled && (
        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1 absolute left-0 top-full">
          <AlertTriangle className="w-3 h-3 shrink-0" />
          Too many lookups. Please slow down and try again shortly.
        </p>
      )}
    </div>
  );
};

export default ValidatedPostcodeInput;
